import {
	ConflictErrorCode,
	NotFoundErrorCode,
	type NotificationTemplateDTO,
	OfferNotificationTemplateChannel,
	ValidationErrorCode,
} from '@today/types'
import type { NotificationTemplateDraft } from '@today/ui-domain'
import { apiErrorOf } from '@/modules/offer-notification/api-error'
import type { NotificationTemplateInput } from '@/services/notification-template.service'

/** A new template's starting values. */
export const EMPTY_DRAFT: NotificationTemplateDraft = {
	name: '',
	description: '',
	channelType: OfferNotificationTemplateChannel.EMAIL,
	locale: '',
	subject: '',
	body: '',
	isDefaultForChannel: false,
	isActive: true,
}

/** A stored template as form values. */
export const draftOf = (template: NotificationTemplateDTO): NotificationTemplateDraft => ({
	name: template.name,
	description: template.description ?? '',
	channelType: template.channelType,
	locale: template.locale,
	subject: template.subject ?? '',
	body: template.body,
	isDefaultForChannel: template.isDefaultForChannel,
	isActive: template.isActive,
})

const orNull = (value: string): string | null => (value.trim() === '' ? null : value)

/** The subject the request carries: only an EMAIL template has one (REQ-002); blank is none. */
export const subjectOf = (draft: NotificationTemplateDraft): string | null =>
	draft.channelType === OfferNotificationTemplateChannel.EMAIL ? orNull(draft.subject) : null

/** The form values as the create/update body; the server judges the bounds (REQ-001). */
export const inputOf = (draft: NotificationTemplateDraft): NotificationTemplateInput => ({
	name: draft.name,
	description: orNull(draft.description),
	channelType: draft.channelType,
	locale: draft.locale,
	subject: subjectOf(draft),
	body: draft.body,
	isDefaultForChannel: draft.isDefaultForChannel,
	isActive: draft.isActive,
})

/** The message a refused save shows next to the form (TO-140 Contract error codes). */
export const templateSaveErrorOf = (error: unknown): string => {
	const { code, field, fields } = apiErrorOf(error)
	switch (code) {
		case ValidationErrorCode.NOTIFICATION_TEMPLATE_INVALID:
			return `Some fields are not valid: ${(fields ?? []).join(', ')}.`
		case ValidationErrorCode.NOTIFICATION_TEMPLATE_SYNTAX_INVALID:
			return `The ${field ?? 'body'} is not valid Liquid.`
		case ValidationErrorCode.NOTIFICATION_TEMPLATE_DEFAULT_INACTIVE:
			return 'An inactive template cannot be the default for its channel.'
		case ConflictErrorCode.NOTIFICATION_TEMPLATE_DEFAULT_CONFLICT:
			return 'Another template was made the default at the same time. Try again.'
		case NotFoundErrorCode.NOTIFICATION_TEMPLATE_NOT_FOUND:
			return 'This template no longer exists.'
		default:
			return 'The template could not be saved. Try again.'
	}
}
